import Head from 'next/head'
import { useContext, useRef } from 'react'
import styled, { ThemeContext } from 'styled-components'

import AddressAutocomplete from 'modules/AddressAutocomplete'
import Testimonials from 'modules/Home/Testimonials'

import Container from 'components/GlobalStyles/Marketing/Container'
import Text from 'components/GlobalStyles/Text'
import RoundedButton from 'components/RoundedButton'
import ComputerMonitor from 'components/SVG/ComputerMonitor'
import Home3 from 'components/SVG/Home3'
import SalesTag from 'components/SVG/SalesTag'
import Spacer from 'components/Spacer'

const FlexCentered = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`

const AddressAutocompleteWrapper = styled(FlexCentered)`
  width: 100%;
`

const AddressInnerWrapper = styled.div`
  min-width: 311px;
  flex-direction: column;
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    min-width: 708px;
    flex-direction: row;
  }
`

const PanelContainer = styled(FlexCentered)`
  width: 100%;
  padding: 48px;
  background: ${props =>
    `linear-gradient(96.53deg, ${props.theme.colors.blue4} 23.14%, ${props.theme.colors.blue} 87.7%)`};
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    padding: 72px;
  }
`

const ValuesContainer = styled(FlexCentered)`
  flex-direction: column;
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    align-items: flex-start;
    flex-direction: row;
  }
`

const ValueContainer = styled(FlexCentered)`
  flex-direction: column;
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    flex-direction: row;
  }
`

const CirlceContainer = styled.div`
  position: relative;
  background-color: ${props => props.theme.colors.white};
  border-radius: 50%;
  box-shadow: 0px 24px 48px rgba(112, 112, 112, 0.32);
  min-width: 64px;
  min-height: 64px;
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    min-width: 96px;
    min-height: 96px;
  }
`

const IconContainer = styled.div`
  width: 32px;
  height: 32px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    width: 48px;
    height: 48px;
  }
`

const ValueText = styled.div`
  font-size: 16px;
  line-height: 22px;
  font-style: normal;
  font-weight: 500;
  padding-top: 2px; // font correction
  color: ${props => props.theme.colors.white};
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    font-size: 18px;
    line-height: 25px;
  }
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    font-size: 20px;
    line-height: 28px;
  }
`

const GetStarted = () => {
  const theme = useContext(ThemeContext)
  const addressInputRef = useRef<HTMLInputElement>(null)
  const getStartedRef = useRef<HTMLDivElement>(null)

  const scrollTo = () => {
    if (getStartedRef.current) {
      getStartedRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    if (addressInputRef.current) addressInputRef.current.focus()
  }

  return (
    <>
      <Head>
        <title>Create Your Free Listing Account - HomeLister</title>
        <meta
          name="description"
          content="Create your free flat fee MLS listing account to sell your home at HomeLister."
        />
        <meta
          name="keywords"
          content="Free Listing Account, Sell My Home, HomeLister"
        />
      </Head>
      <Container>
        <Spacer
          size={32}
          tabletSize={48}
          desktopSize={56}
        />
        <Text
          fontSize={24}
          tabletFontSize={32}
          desktopFontSize={40}
          lineHeight="33px"
          tabletLineHeight="44px"
          desktopLineHeight="55px"
          fontWeight={800}
          color={theme.colors.neutral900}
          textAlign="center"
        >
          See how much you can save.
        </Text>
        <Spacer size={16} />
        <Text
          fontSize={16}
          tabletFontSize={18}
          desktopFontSize={20}
          lineHeight="22px"
          tabletLineHeight="25px"
          desktopLineHeight="24px"
          fontWeight={500}
          color={theme.colors.neutral400}
          textAlign="center"
        >
          Enter your address below to create your free listing account. Savings await...
        </Text>
        <Spacer
          size={36}
          desktopSize={56}
          desktopLargeSize={56}
        />
        <AddressAutocompleteWrapper ref={getStartedRef}>
          <AddressInnerWrapper>
            <AddressAutocomplete
              buttonBackground={theme.colors.gold600}
              buttonBackgroundHover={theme.colors.yellow2}
              buttonFontSize={12}
              inputBackground={theme.colors.neutral25}
              dropShadow={`0px 8px 24px ${theme.colors.transparentGrey23Alpha16}`}
              width="100%"
              buttonDesktopWidth={260}
              addressInputRef={addressInputRef}
              flexDirectionTablet="column"
              errorTopPosition="130px"
            />
          </AddressInnerWrapper>
        </AddressAutocompleteWrapper>
      </Container>
      <Spacer
        size={32}
        tabletSize={48}
        desktopSize={56}
        desktopLargeSize={56}
      />
      <PanelContainer>
        <ValuesContainer>
          <ValueContainer>
            <CirlceContainer>
              <IconContainer>
                <ComputerMonitor
                  width="100%"
                  height="100%"
                  fill={theme.colors.gold600}
                />
              </IconContainer>
            </CirlceContainer>
            <Spacer
              size={16}
              desktopSize={24}
              desktopLargeSize={24}
            />
            <ValueText>Help from experts.</ValueText>
          </ValueContainer>
          <Spacer
            size={24}
            desktopSize={48}
            desktopLargeSize={48}
          />
          <ValueContainer>
            <CirlceContainer>
              <IconContainer>
                <SalesTag
                  width="100%"
                  height="100%"
                  fill={theme.colors.gold600}
                />
              </IconContainer>
            </CirlceContainer>
            <Spacer
              size={16}
              desktopSize={24}
              desktopLargeSize={24}
            />
            <ValueText>Low flat fees.</ValueText>
          </ValueContainer>
          <Spacer
            size={24}
            desktopSize={48}
            desktopLargeSize={48}
          />
          <ValueContainer>
            <CirlceContainer>
              <IconContainer>
                <Home3
                  width="100%"
                  height="100%"
                  fill={theme.colors.gold600}
                />
              </IconContainer>
            </CirlceContainer>
            <Spacer
              size={16}
              desktopSize={24}
              desktopLargeSize={24}
            />
            <ValueText>Exceptional service.</ValueText>
          </ValueContainer>
        </ValuesContainer>
      </PanelContainer>
      <Testimonials
        scrollTo={scrollTo}
        customButton={(
          <RoundedButton
            text="Get Started Today"
            color={theme.colors.white}
            background={theme.colors.blue500}
            hoverBackground={theme.colors.blue9}
            boxShadow={`0px 12px 16px ${theme.colors.transparentBlue4Alpha25}`}
            fontSize={12}
            radius={50}
            height={44}
            width={230}
            onClick={scrollTo}
          />
        )}
      />
    </>
  )
}

export default GetStarted
